import {
  ActiveChallengeCard,
  NoFriendCard,
  ParticipationSettings,
  WaitingCard,
  getUserIntensity,
  intensityConfig,
} from "@/components/buddies";
import { PastMatchups } from "@/components/buddies/past-matchups";
import { useCurrentUser } from "@/data/auth/hooks";
import { useBuddyMatchups, useUpdateWeeklyGoal } from "@/data/buddies/hooks";
import { Buddy, GoalIntensity, Matchup } from "@/data/buddies/types";
import dayjs from "@/lib/dayjs";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

export const Route = createFileRoute("/buddies")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data: currentUserData } = useCurrentUser();
  const { data: buddiesData, isLoading } = useBuddyMatchups();

  const updateWeeklyGoal = useUpdateWeeklyGoal();

  const [intensity, setIntensity] = useState<GoalIntensity | null>(
    (buddiesData?.weeklyGoal?.calories as GoalIntensity) || null
  );

  // State for tracking participation status
  const [isParticipating, setIsParticipating] = useState(
    !!buddiesData?.weeklyGoal?.calories
  );

  // State to track if user has seen the "No Friend Found" screen
  const [hasSeenNoFriendFound, setHasSeenNoFriendFound] = useState(false);

  // Update intensity and participation when data loads
  useEffect(() => {
    if (buddiesData?.weeklyGoal) {
      setIntensity((buddiesData.weeklyGoal.calories as GoalIntensity) || null);
      setIsParticipating(!!buddiesData.weeklyGoal.calories);
    }
  }, [buddiesData?.weeklyGoal]);

  // Get current week ID (YYYY + "W" + week number)
  const currentWeekId = dayjs().format("YYYY") + "W" + dayjs().isoWeek();

  // Find current week's matchup - strictly based on the weekId
  const currentMatchup = useMemo(() => {
    if (!buddiesData?.matchups || buddiesData.matchups.length === 0)
      return null;

    // Find an exact match for the current week
    return buddiesData.matchups.find((m) => m.weekId === currentWeekId) || null;
  }, [buddiesData?.matchups, currentWeekId]);

  // Determine challenge status
  const challengeStatus = useMemo(() => {
    // If there's a current matchup, always show it as active regardless of participation status
    if (currentMatchup) return "active";

    // If not participating, show waiting
    if (!isParticipating) return "waiting";

    // If user has already seen the no-friend screen and clicked "Back to Waiting"
    if (hasSeenNoFriendFound) return "waiting";

    // Check if the weeklyGoal was updated in a previous week
    // Only show "no-friend" if the user toggled participation on in a previous week
    if (buddiesData?.weeklyGoal?.updatedAt) {
      const updatedAtWeek = dayjs(buddiesData.weeklyGoal.updatedAt).isoWeek();
      const updatedAtYear = dayjs(buddiesData.weeklyGoal.updatedAt).year();
      const currentWeek = dayjs().isoWeek();
      const currentYear = dayjs().year();

      // If the goal was updated in a previous week and there's no matchup, show "no-friend"
      if (
        updatedAtYear < currentYear ||
        (updatedAtYear === currentYear && updatedAtWeek < currentWeek)
      ) {
        return "no-friend";
      }
    }

    // Otherwise, show waiting
    return "waiting";
  }, [
    currentMatchup,
    isParticipating,
    buddiesData?.weeklyGoal?.updatedAt,
    hasSeenNoFriendFound,
  ]) as "waiting" | "active" | "no-friend";

  // Get past matchups (excluding the current active matchup)
  const pastMatchups = useMemo(() => {
    if (!buddiesData?.matchups || !currentUserData) return [];

    // Filter out current matchup if it exists
    const filteredMatchups = currentMatchup
      ? buddiesData.matchups.filter((m) => m.weekId !== currentMatchup.weekId)
      : buddiesData.matchups;

    // Sort matchups by week ID in descending order (most recent first)
    return [...filteredMatchups].sort((a, b) => {
      // Parse week IDs to compare
      const [aYearStr, aWeekStr] = a.weekId.split("W");
      const [bYearStr, bWeekStr] = b.weekId.split("W");

      const aYear = parseInt(aYearStr);
      const bYear = parseInt(bYearStr);

      if (aYear !== bYear) return bYear - aYear;

      const aWeek = parseInt(aWeekStr);
      const bWeek = parseInt(bWeekStr);

      return bWeek - aWeek;
    });
  }, [buddiesData?.matchups, currentUserData, currentMatchup]);

  const getCurrentUserFromMatchup = (matchup: Matchup): Buddy => {
    const isInitiator = matchup.initiator.id === currentUserData?.id;

    return {
      ...(isInitiator ? { ...matchup.initiator } : { ...matchup.matched }),
      progress: isInitiator
        ? matchup.initiatorProgress
        : matchup.matchedProgress,
      goal: isInitiator ? matchup.initiatorGoal : matchup.matchedGoal,
      intensity: isInitiator
        ? getUserIntensity(matchup.initiatorGoal)
        : getUserIntensity(matchup.matchedGoal),
    };
  };

  const getOtherUserFromMatchup = (matchup: Matchup): Buddy => {
    const isInitiator = matchup.initiator.id === currentUserData?.id;

    return {
      ...(isInitiator ? { ...matchup.matched } : { ...matchup.initiator }),
      progress: isInitiator
        ? matchup.matchedProgress
        : matchup.initiatorProgress,
      goal: isInitiator ? matchup.matchedGoal : matchup.initiatorGoal,
      intensity: isInitiator
        ? getUserIntensity(matchup.matchedGoal)
        : getUserIntensity(matchup.initiatorGoal),
    };
  };

  const handleSetIntensity = (newIntensity: GoalIntensity) => {
    setIntensity(newIntensity);

    updateWeeklyGoal.mutate({
      calories: newIntensity,
    });
  };

  const handleParticipationToggle = (participate: boolean) => {
    setIsParticipating(participate);
    if (!participate) {
      setIntensity(null);

      updateWeeklyGoal.mutate({
        calories: null,
      });
    } else if (!intensity) {
      const newIntensity: GoalIntensity = "MEDIUM";
      setIntensity(newIntensity);

      updateWeeklyGoal.mutate({
        calories: newIntensity,
      });
    }
  };

  return (
    <div className="container p-4 bg-gradient-to-br from-[#2b7fff]/10 to-[#fe9a00]/10 w-full h-full">
      <div className="flex items-center sm:items-start justify-between mb-6 flex-col sm:flex-row gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#2b7fff] to-[#fe9a00] bg-clip-text text-transparent">
            Buddies
          </h1>
          <p className="text-gray-500">
            Connect with friends and challenge each other to reach your weekly
            fitness goals.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto">
        <div className="w-full mb-8">
          <ParticipationSettings
            isParticipating={isParticipating}
            intensity={intensity}
            onParticipationToggle={handleParticipationToggle}
            onSetIntensity={handleSetIntensity}
          />
        </div>

        {/* Weekly Match-Up Section */}
        {!currentUserData || isLoading ? (
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2b7fff]"></div>
          </div>
        ) : currentMatchup ? (
          <ActiveChallengeCard
            user={getCurrentUserFromMatchup(currentMatchup)}
            matchedFriend={getOtherUserFromMatchup(currentMatchup)}
            isOneWay={currentMatchup.isOneWay}
          />
        ) : !isParticipating ? (
          <></>
        ) : challengeStatus === "waiting" ? (
          <WaitingCard
            user={{
              id: currentUserData.id,
              firstName: currentUserData.firstName,
              lastName: currentUserData.lastName,
              avatarUrl: currentUserData.avatarUrl,
              progress: 0,
              goal: intensity ? intensityConfig[intensity].value : 0,
              intensity,
            }}
            intensity={intensity}
          />
        ) : challengeStatus === "no-friend" ? (
          <NoFriendCard
            onBackToWaiting={() => {
              setHasSeenNoFriendFound(true);
            }}
          />
        ) : (
          <div className="p-8 text-center text-gray-500">
            Loading your buddy matchup...
          </div>
        )}

        {/* Past Matchups Section */}
        {pastMatchups.length > 0 && (
          <PastMatchups pastMatchups={pastMatchups} />
        )}
      </div>
    </div>
  );
}
